import { QueryClient } from '@tanstack/react-query';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import type { ApiError } from '../services/api';

// Default query options with advanced caching features
const defaultQueryOptions = {
  queries: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    // Stale-while-revalidate: show stale data while fetching fresh data
    refetchOnMount: 'always' as const,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    // Background refresh for better UX
    refetchInterval: undefined, // Can be overridden per query
    refetchIntervalInBackground: false,
    retry: (failureCount: number, error: unknown) => {
      const apiError = error as ApiError;
      
      // Don't retry on authentication errors
      if (apiError.status === 401 || apiError.status === 403) {
        return false;
      }
      
      // Don't retry on client errors (4xx)
      if (apiError.status && apiError.status >= 400 && apiError.status < 500) {
        return false;
      }
      
      // Retry up to 3 times for server errors and network errors
      return failureCount < 3;
    },
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
    // Network mode for offline support
    networkMode: 'online' as const,
  },
  mutations: {
    retry: false, // Don't retry mutations by default
    networkMode: 'online' as const,
  },
};

// Create query client instance
export const queryClient = new QueryClient({
  defaultOptions: defaultQueryOptions,
});

// Create persister for offline cache support
const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'REACT_QUERY_OFFLINE_CACHE',
  serialize: JSON.stringify,
  deserialize: JSON.parse,
});

// Setup cache persistence with selective persistence
persistQueryClient({
  queryClient,
  persister,
  maxAge: 1000 * 60 * 60 * 24, // 24 hours
  // Only persist specific query keys
  dehydrateOptions: {
    shouldDehydrateQuery: (query) => {
      // Don't persist queries that are too fresh or have errors
      if (query.state.status === 'error') return false;
      
      // Persist user data, profile, and jobs for offline access
      const queryKey = query.queryKey[0] as string;
      const persistableKeys = ['auth', 'jobs', 'resumes', 'profile'];
      return persistableKeys.includes(queryKey);
    },
  },
});

// Query keys factory for consistent key management
export const queryKeys = {
  // Authentication
  auth: {
    currentUser: ['auth', 'currentUser'] as const,
    profile: ['auth', 'profile'] as const,
  },
  
  // Jobs
  jobs: {
    all: ['jobs'] as const,
    lists: () => [...queryKeys.jobs.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.jobs.lists(), filters] as const,
    details: () => [...queryKeys.jobs.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.jobs.details(), id] as const,
    featured: () => [...queryKeys.jobs.all, 'featured'] as const,
    similar: (id: string) => [...queryKeys.jobs.all, 'similar', id] as const,
  },
  
  // Resumes
  resumes: {
    all: ['resumes'] as const,
    lists: () => [...queryKeys.resumes.all, 'list'] as const,
    list: (userId?: string) => [...queryKeys.resumes.lists(), userId] as const,
    details: () => [...queryKeys.resumes.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.resumes.details(), id] as const,
    status: (id: string) => [...queryKeys.resumes.all, 'status', id] as const,
  },
  
  // Applications
  applications: {
    all: ['applications'] as const,
    lists: () => [...queryKeys.applications.all, 'list'] as const,
    list: (userId?: string) => [...queryKeys.applications.lists(), userId] as const,
    details: () => [...queryKeys.applications.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.applications.details(), id] as const,
    timeline: (userId: string) => [...queryKeys.applications.all, 'timeline', userId] as const,
  },
  
  // Messages
  messages: {
    all: ['messages'] as const,
    conversations: () => [...queryKeys.messages.all, 'conversations'] as const,
    conversation: (userId: string) => [...queryKeys.messages.conversations(), userId] as const,
    thread: (conversationId: string) => [...queryKeys.messages.all, 'thread', conversationId] as const,
  },
  
  // Admin
  admin: {
    all: ['admin'] as const,
    users: () => [...queryKeys.admin.all, 'users'] as const,
    candidates: () => [...queryKeys.admin.all, 'candidates'] as const,
    analytics: () => [...queryKeys.admin.all, 'analytics'] as const,
    systemSettings: () => [...queryKeys.admin.all, 'systemSettings'] as const,
  },
  
  // Theme
  theme: {
    all: ['theme'] as const,
    current: () => [...queryKeys.theme.all, 'current'] as const,
    available: () => [...queryKeys.theme.all, 'available'] as const,
  },
} as const;

// Utility function to invalidate related queries
export const invalidateQueries = {
  auth: () => queryClient.invalidateQueries({ queryKey: queryKeys.auth.currentUser }),
  jobs: () => queryClient.invalidateQueries({ queryKey: queryKeys.jobs.all }),
  resumes: () => queryClient.invalidateQueries({ queryKey: queryKeys.resumes.all }),
  applications: () => queryClient.invalidateQueries({ queryKey: queryKeys.applications.all }),
  messages: () => queryClient.invalidateQueries({ queryKey: queryKeys.messages.all }),
  admin: () => queryClient.invalidateQueries({ queryKey: queryKeys.admin.all }),
  theme: () => queryClient.invalidateQueries({ queryKey: queryKeys.theme.all }),
};